import { inject, Injectable } from '@angular/core';
import {
  LayerEditEvent,
  LayerExtraData,
  MapMarker,
  MapInput,
} from '@features/map/interfaces';
import { RouteNetwork } from '@shared/interfaces/route-network';
import { map, Observable, of, tap } from 'rxjs';
import { RouteNetworkDataService } from './route-network-data.service';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
@Injectable({ providedIn: 'root' })
export class RouteNetworkLayerBuilderService {
  private routeNetworkDataService = inject(RouteNetworkDataService);
  private snackbar = inject(DvSnackbarService);
  private translate = inject(TranslateService);

  private colorIndex = 0;

  mapRouteNetworkToMapMarkerInput(
    networks: RouteNetwork[],
    editable = false,
    segmentRightClickFn?: (e: L.LeafletMouseEvent) => void,
    enableHoverAnimation = false
  ): MapInput[] {
    const result: MapInput[] = [];

    networks.forEach(network => {
      network.RouteSegments?.forEach(segment => {
        if (!segment.Lines || segment.Lines.length === 0) return;

        const sortedLines = [...segment.Lines].sort(
          (a, b) => (a.SequenceNo ?? 0) - (b.SequenceNo ?? 0)
        );
        const markers: MapMarker[] = sortedLines.map(
          line =>
            ({
              lat: line.Latitude ?? 0,
              lng: line.Longitude ?? 0,
              options: {
                isToolTipPermanent: false,
              },
              extraData: {
                editable,
                id: line.RouteSegmentLineID,
                polylineSegmentId: line.RouteSegmentID,
                extraDataForLayerGetterFn: this.newPointInsertFn,
              } satisfies LayerExtraData,
            }) satisfies MapMarker
        );

        result.push({
          layerRightClickFn: segmentRightClickFn,
          isRouteInput: true,
          hideRouteIcons: true,
          enableHoverAnimation,
          routeColor: editable
            ? 'var(--dv-border-brand)'
            : 'var(--dv-border-inverse)',
          points: markers,
        });
      });
    });

    return result;
  }

  private newPointInsertFn = (
    layerEditEvent: LayerEditEvent
  ): Observable<LayerExtraData> => {
    const marker = layerEditEvent.markers[0];
    return marker.extraData
      ? this.routeNetworkDataService
          .updateLines([
            {
              Latitude: marker.lat,
              Longitude: marker.lng,
              RouteSegmentID: marker.extraData.polylineSegmentId,
              SequenceNo: layerEditEvent.index,
            },
          ])
          .pipe(
            tap(() => {
              this.snackbar.open(
                this.translate.instant(
                  'RouteNetwork.RouteSegmentLineAddSuccess'
                ),
                SnackBarConfig.Success
              );
            }),
            map(resp => {
              return {
                ...marker.extraData,
                ...(resp.length
                  ? {
                      id: resp[0]?.RouteSegmentLineID,
                      polylineSegmentId: resp[0]?.RouteSegmentID,
                    }
                  : undefined),
              };
            })
          )
      : of({});
  };
}
